import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ServiceSelector = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: 'emergency',
      title: 'Emergency Connectivity Issues',
      description: 'Immediate support for critical network failures and connectivity problems',
      icon: 'AlertTriangle',
      color: 'red',
      urgency: 'Urgent',
      response: '2-4 Hours',
      route: '/get-quote-consultation'
    },
    {
      id: 'upgrade',
      title: 'Planned Infrastructure Upgrade',
      description: 'Strategic transition from copper to fiber with comprehensive planning',
      icon: 'TrendingUp',
      color: 'blue',
      urgency: 'Planned',
      response: '1-2 Days',
      route: '/copper-to-fiber-conversion-service'
    },
    {
      id: 'construction',
      title: 'New Construction',
      description: 'Future-ready fiber installation for new buildings and developments',
      icon: 'Building',
      color: 'emerald',
      urgency: 'Project',
      response: '3-5 Days',
      route: '/copper-to-fiber-conversion-service'
    },
    {
      id: 'optimization',
      title: 'Performance Optimization',
      description: 'Enhance existing network performance and reliability',
      icon: 'Gauge',
      color: 'amber',
      urgency: 'Enhancement',
      response: '1-3 Days',
      route: '/copper-to-fiber-conversion-service'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        badge: 'bg-red-100 text-red-800',
        hover: 'hover:bg-red-100'
      },
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-800',
        hover: 'hover:bg-blue-100'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        icon: 'text-emerald-600',
        badge: 'bg-emerald-100 text-emerald-800',
        hover: 'hover:bg-emerald-100'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        icon: 'text-amber-600',
        badge: 'bg-amber-100 text-amber-800',
        hover: 'hover:bg-amber-100'
      }
    };
    return colorMap?.[color];
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
            Choose Your Path to Better Connectivity
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Select your specific situation to get tailored solutions and immediate next steps
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services?.map((service) => {
            const colors = getColorClasses(service?.color);
            const isSelected = selectedService === service?.id;
            
            return (
              <div
                key={service?.id}
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                  isSelected 
                    ? `${colors?.bg} ${colors?.border} shadow-brand-moderate` 
                    : `bg-white border-slate-200 hover:border-slate-300 ${colors?.hover}`
                }`}
                onClick={() => setSelectedService(service?.id)}
              >
                {/* Urgency Badge */}
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-4 ${colors?.badge}`}>
                  {service?.urgency}
                </div>
                {/* Icon */}
                <div className={`w-12 h-12 rounded-lg ${colors?.bg} flex items-center justify-center mb-4`}>
                  <Icon name={service?.icon} size={24} className={colors?.icon} />
                </div>
                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {service?.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {service?.description}
                  </p>
                  
                  {/* Response Time */}
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Clock" size={16} className="text-slate-400" />
                    <span className="text-slate-600">Response: {service?.response}</span>
                  </div>
                </div>
                {/* Action Button */}
                {isSelected && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <Link to={service?.route}>
                      <Button 
                        variant="default" 
                        size="sm" 
                        iconName="ArrowRight" 
                        iconPosition="right"
                        fullWidth
                        className="animate-brand-smooth"
                      >
                        Get Started
                      </Button>
                    </Link>
                  </div>
                )}
                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={16} className="text-white" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        {/* <div className="text-center mt-12">
          <div className="bg-slate-50 rounded-xl p-8 border">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">
              Not sure which option fits your needs?
            </h3>
            <p className="text-slate-600 mb-6">
              Our experts can help you identify the best approach for your specific situation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/network-assessment-tool">
                <Button 
                  variant="outline" 
                  iconName="MessageSquare" 
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Free Consultation
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                iconName="Phone" 
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Call: 01483 343 064
              </Button>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default ServiceSelector;